import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationsService, grantsService } from '../services/api';
import NoteForm from './NoteForm';
import AuditHistory from './AuditHistory';
import AttachmentsTab from './Attachments';
import './OrganizationDetail.css';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import PhoneEditor from './PhoneEditor';
import AddressEditor from './AddressEditor';

const OrganizationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, accessToken } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditForm, setShowEditForm] = useState(false);
  const [attachmentCount, setAttachmentCount] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    phones: [],
    addresses: []
  });

  const fetchOrganization = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await organizationsService.get(id);
      setOrganization(response.data);
      setFormData({
        name: response.data.name || '',
        phones: response.data.phones || [],
        addresses: response.data.addresses || []
      });
      
      // Fetch attachment count
      try {
        const attachmentResponse = await axios.get(
          `/api/attachments?entityType=organization&entityId=${id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
        setAttachmentCount(attachmentResponse.data.data?.length || 0);
      } catch (attachmentErr) {
        console.error('Error fetching attachments:', attachmentErr);
        setAttachmentCount(0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch organization details');
      console.error('Error fetching organization:', err);
    } finally {
      setLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  useEffect(() => {
    const fetchGrants = async () => {
      try {
        const response = await grantsService.list({
          organization_id: id,
          limit: 100
        });
        setGrants(response.data.data || []);
      } catch (err) {
        console.error('Error fetching grants:', err);
      }
    };

    if (id) {
      fetchGrants();
    }
  }, [id]);

  const handleDeleteOrganization = async () => {
    if (window.confirm('Are you sure you want to delete this organization?')) {
      try {
        await organizationsService.delete(id);
        alert('Organization deleted successfully');
        navigate('/organizations');
      } catch (err) {
        alert('Failed to delete organization: ' + err.message);
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhonesChange = (phones) => {
    setFormData(prev => ({
      ...prev,
      phones
    }));
  };

  const handleAddressesChange = (addresses) => {
    setFormData(prev => ({
      ...prev,
      addresses
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      await organizationsService.update(id, formData);
      alert('Organization updated successfully');
      setShowEditForm(false);
      fetchOrganization();
    } catch (err) {
      alert('Failed to update organization: ' + err.message);
    }
  };

  const handleFormCancel = () => {
    setShowEditForm(false);
    // Reset form to current organization data
    if (organization) {
      setFormData({
        name: organization.name || '',
        phones: organization.phones || '',
        addresses: organization.addresses || ''
      });
    }
  };

  const getGrantStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'applied':
        return '#ff9800';
      case 'approved':
        return '#4caf50';
      case 'rejected':
        return '#f44336';
      case 'in_progress':
        return '#2196f3';
      case 'funded':
        return '#4caf50';
      default:
        return '#999';
    }
  };

  if (loading) return <div className="loading">Loading organization details...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!organization) return <div className="alert alert-error">Organization not found</div>;

  const totalGrantAmount = grants.reduce((sum, g) => sum + (parseFloat(g.amount_requested) || 0), 0);
  const fundedCount = grants.filter(g => g.status?.toLowerCase() === 'funded').length;
  const approvedCount = grants.filter(g => g.status?.toLowerCase() === 'approved').length;

  return (
    <div className="organization-detail-container">
      <div className="detail-header">
        <button className="btn btn-outline" onClick={() => navigate('/organizations')}>
          ← Back to Organizations
        </button>
        <div className="header-content">
          <h1>{organization.name}</h1>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowEditForm(true)}
          >
            Edit Organization
          </button>
          {isAdmin && (<button 
              className="btn btn-danger"
              onClick={handleDeleteOrganization}
            >
              Delete
            </button>)}
        </div>
      </div>

      {showEditForm && (
        <div className="edit-form-container">
          <div className="form-overlay" onClick={handleFormCancel} />
          <div className="edit-form">
            <h2>Edit Organization</h2>
            <form onSubmit={handleFormSubmit}>
              <div className="form-group">
                <label htmlFor="name">Organization Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-section">
                <h3>Contact Information</h3>
                
                <PhoneEditor
                  phones={formData.phones}
                  onChange={handlePhonesChange}
                />
                
                <AddressEditor
                  addresses={formData.addresses}
                  onChange={handleAddressesChange}
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-outline"
                  onClick={handleFormCancel}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="detail-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'grants' ? 'active' : ''}`}
          onClick={() => setActiveTab('grants')}
        >
          Grants ({grants.length})
        </button>
        <button 
          className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes ({organization.notes?.length || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'attachments' ? 'active' : ''}`}
          onClick={() => setActiveTab('attachments')}
        >
          Attachments ({attachmentCount})
        </button>
        {isAdmin && (
          <button 
            className={`tab ${activeTab === 'audit' ? 'active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            Audit History
          </button>
        )}
      </div>

      {activeTab === 'overview' && (
        <div className="detail-section">
          <div className="info-grid">
            <div className="info-card">
              <label>Organization Name</label>
              <p>{organization.name}</p>
            </div>

            {organization.phones && organization.phones.length > 0 && (
              <div className="info-card">
                <label>Phone</label>
                <p>
                  {organization.phones.map((phone, index) => (
                    <div key={index}>
                      {phone.phone_number} ({phone.phone_type})
                    </div>
                  ))}
                </p>
              </div>
            )}

            {organization.addresses && organization.addresses.length > 0 && (
              <div className="info-card">
                <label>Address</label>
                <p>
                  {organization.addresses.map((address, index) => (
                    <div key={index}>
                      {address.street_address && <div>{address.street_address}</div>}
                      {(address.city || address.state) && (
                        <div>{address.city}, {address.state} {address.zip_code}</div>
                      )}
                      {address.country && <div>{address.country}</div>}
                    </div>
                  ))}
                </p>
              </div>
            )}

            <div className="info-card">
              <label>Total Grants</label>
              <p>{grants.length}</p>
            </div>

            <div className="info-card">
              <label>Total Grant Amount Requested</label>
              <p className="amount">${totalGrantAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            </div>

            <div className="info-card">
              <label>Funded Grants</label>
              <p>{fundedCount}</p>
            </div>

            <div className="info-card">
              <label>Approved Grants</label>
              <p>{approvedCount}</p>
            </div>

            <div className="info-card">
              <label>Member Since</label>
              <p>{new Date(organization.created_at).toLocaleDateString()}</p>
            </div>

            <div className="info-card">
              <label>Last Updated</label>
              <p>{new Date(organization.updated_at).toLocaleDateString()}</p>
            </div>
          </div>

          {organization.mission_statement && (
            <div className="description-section">
              <h3>Mission Statement</h3>
              <p>{organization.mission_statement}</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'grants' && (
        <div className="detail-section">
          <div className="section-header">
            <h2>Associated Grants</h2>
          </div>

          {grants.length > 0 ? (
            <div className="table-container">
              <table className="grants-table">
                <thead>
                  <tr>
                    <th>Grant Name</th>
                    <th>Amount Requested</th>
                    <th>Date Applied</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grants.map(grant => (
                    <tr key={grant.id}>
                      <td>{grant.name}</td>
                      <td className="amount">
                        {grant.is_in_kind ? (
                          <span className="in-kind-label">In-Kind</span>
                        ) : grant.amount_requested ? (
                          `$${Number(grant.amount_requested).toLocaleString('en-US', { 
                            minimumFractionDigits: 2, 
                            maximumFractionDigits: 2 
                          })}`
                        ) : '-'}
                      </td>
                      <td>{new Date(grant.date_applied).toLocaleDateString()}</td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ backgroundColor: getGrantStatusColor(grant.status) }}
                        >
                          {grant.status}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-primary"
                          onClick={() => navigate(`/grants/${grant.id}`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No grants associated with this organization yet.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="detail-section">
          <NoteForm 
            entityId={parseInt(id)} 
            entityType="organization"
            onNoteAdded={fetchOrganization}
          />
        </div>
      )}

      {activeTab === 'attachments' && (
        <AttachmentsTab 
          entityType="organization" 
          entityId={parseInt(id)}
          token={accessToken}
        />
      )}

      {activeTab === 'audit' && isAdmin && (
        <AuditHistory entityId={id} entityType="organization" />
      )}
    </div>
  );
};

export default OrganizationDetail;
